import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getMyRooms, deleteRoom } from '../services/roomApi';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import useCopyToClipboard from '../hooks/useCopyToClipboard';

function MyRooms() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCopyCode, setCurrentCopyCode] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);

  const { copied, copyToClipboard } = useCopyToClipboard();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchRooms = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        const data = await getMyRooms();
        setRooms(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRooms();
  }, [user]);

  const handleCopyLink = (room) => {
    const url = `${window.location.origin}/room/${room.inviteCode}`;
    setCurrentCopyCode(room.inviteCode);
    copyToClipboard(url);
  };

  const handleDelete = async (room) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${room.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      setDeletingCode(room.inviteCode);
      await deleteRoom(room.inviteCode);
      setRooms(rooms.filter((r) => r.inviteCode !== room.inviteCode));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingCode(null);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-puzzle-text">My Puzzle Rooms</h1>
          <Link
            to="/create"
            className="bg-puzzle-blue text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-600 transition-colors"
          >
            + Create Puzzle
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {rooms.length === 0 ? (
          <div className="bg-puzzle-surface rounded-2xl shadow-lg p-8 text-center border border-puzzle-border">
            <div className="text-6xl mb-4">🧩</div>
            <h2 className="text-xl font-semibold text-puzzle-text mb-2">No Rooms Yet</h2>
            <p className="text-puzzle-text-secondary mb-6">
              Create a puzzle and share it with friends by creating a room.
            </p>
            <Link
              to="/create"
              className="inline-block bg-puzzle-blue text-white px-6 py-3 rounded-full font-medium hover:bg-blue-600 transition-colors"
            >
              Create Your First Puzzle
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {rooms.map((room) => (
              <div
                // eslint-disable-next-line no-underscore-dangle
                key={room._id}
                className="bg-puzzle-surface rounded-xl shadow p-4 border border-puzzle-border"
              >
                <div className="flex items-start gap-4">
                  {/* Puzzle Preview */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-puzzle-muted flex-shrink-0">
                    {room.puzzle?.state?.previewImage ? (
                      <img
                        src={room.puzzle.state.previewImage}
                        alt={room.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-puzzle-text-secondary">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Room Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-puzzle-text truncate">{room.name}</h3>
                        <p className="text-sm text-puzzle-text-secondary">
                          {(room.puzzle?.config?.rows ?? 0) * (room.puzzle?.config?.columns ?? 0) || '?'}
                          {' '}
                          pieces
                        </p>
                      </div>

                      {/* Stats */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-bold text-puzzle-blue">
                          {room.completionCount || 0}
                        </div>
                        <div className="text-xs text-puzzle-text-secondary">completed</div>
                      </div>
                    </div>

                    {/* Participant count */}
                    <div className="flex items-center gap-4 mt-2 text-sm text-puzzle-text-secondary">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {room.participantCount || 0}
                        {' '}
                        players
                      </span>
                      {room.expiresAt && (
                        <span className="text-orange-500">
                          Expires:
                          {' '}
                          {new Date(room.expiresAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => handleCopyLink(room)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-puzzle-muted text-puzzle-text text-sm rounded-lg hover:bg-puzzle-border transition-colors"
                      >
                        {copied && currentCopyCode === room.inviteCode ? (
                          <>
                            <svg className="w-4 h-4 text-puzzle-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy Link
                          </>
                        )}
                      </button>

                      <Link
                        to={`/room/${room.inviteCode}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-puzzle-blue text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </Link>

                      <button
                        type="button"
                        onClick={() => handleDelete(room)}
                        disabled={deletingCode === room.inviteCode}
                        className="flex items-center gap-1 px-3 py-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm rounded-lg transition-colors disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {deletingCode === room.inviteCode ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyRooms;
